/**
 * AutoGenerationPanel 组件
 * 
 * 自动生成模式的左侧导航面板，显示步骤列表和状态指示器
 * 
 * @module components/projectSettings/AutoGenerationPanel
 * @see Requirements 5.1-5.6, 10.2-10.6
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpenIcon,
  GlobeAltIcon,
  PencilSquareIcon,
  ListBulletIcon,
  PaintBrushIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  DocumentTextIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import {
  GenerationStepType,
  ProjectSettingStepStatus,
  GENERATION_STEP_ORDER,
  GENERATION_STEP_NAMES,
  TOTAL_GENERATION_STEPS,
  getLabelsByLocale,
  Locale
} from '@packages/global';

/**
 * 步骤图标映射（不包含 PROJECT_META）
 */
const STEP_ICONS: Record<GenerationStepType, React.ComponentType<{ className?: string }>> = {
  [GenerationStepType.PROJECT_META]: BookOpenIcon,
  [GenerationStepType.STORY_SYNOPSIS]: BookOpenIcon,
  [GenerationStepType.WORLDVIEW]: GlobeAltIcon,
  [GenerationStepType.WRITING_STYLE]: PencilSquareIcon,
  [GenerationStepType.EPISODE_OUTLINE]: ListBulletIcon,
  [GenerationStepType.ART_STYLE]: PaintBrushIcon,
  [GenerationStepType.SUBJECT]: UserGroupIcon,
  [GenerationStepType.NOVEL_PARSE]: DocumentTextIcon,
  [GenerationStepType.NOVEL_SPLIT]: SparklesIcon,
  [GenerationStepType.EPISODE_SCRIPT]: DocumentTextIcon,
};

/**
 * 显示的步骤列表（不包含 PROJECT_META、NOVEL_PARSE、NOVEL_SPLIT）
 */
const DISPLAY_STEPS = GENERATION_STEP_ORDER.filter(
  step => step !== GenerationStepType.PROJECT_META
    && step !== GenerationStepType.NOVEL_PARSE
    && step !== GenerationStepType.NOVEL_SPLIT
);

interface AutoGenerationPanelProps {
  currentStep: GenerationStepType | null;
  completedSteps: GenerationStepType[];
  failedStep: GenerationStepType | null;
  isActive: boolean;
  projectTitle?: string;
  onStepClick: (step: GenerationStepType) => void;
  onCancel: () => void;
}

/**
 * 进度指示器组件
 */
function ProgressIndicator({ completed, total, t }: { completed: number; total: number; t: (key: string) => string }) {
  const percentage = Math.round((completed / total) * 100);

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-xs">
        <span className="text-gray-300">{t('autoGen.progress')}</span>
        <span className="text-gray-50 font-medium">{completed}/{total}</span>
      </div>
      <div className="h-1.5 bg-border-subtle rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

/**
 * 步骤项组件
 */
function StepItem({
  step,
  index,
  status,
  isCurrent,
  onClick,
  disabled,
  stepNames
}: {
  step: GenerationStepType;
  index: number;
  status: ProjectSettingStepStatus;
  isCurrent: boolean;
  onClick: () => void;
  disabled: boolean;
  stepNames: Record<string, string>;
}) {
  const Icon = STEP_ICONS[step];
  const name = stepNames[step];

  // 状态样式
  const getStatusStyles = () => {
    if (status === ProjectSettingStepStatus.COMPLETED) {
      return {
        bg: 'bg-success/20',
        border: 'border-success/30',
        text: 'text-success',
        icon: <CheckCircleIcon className="w-4 h-4 text-success" />
      };
    }
    if (status === ProjectSettingStepStatus.FAILED) {
      return {
        bg: 'bg-error/20',
        border: 'border-error/30',
        text: 'text-error',
        icon: <XCircleIcon className="w-4 h-4 text-error" />
      };
    }
    if (status === ProjectSettingStepStatus.IN_PROGRESS) {
      return {
        bg: 'bg-primary/10',
        border: 'border-primary/50',
        text: 'text-primary',
        icon: (
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        )
      };
    }
    return {
      bg: 'bg-transparent',
      border: 'border-border-default',
      text: 'text-gray-400',
      icon: <span className="w-4 h-4 text-center text-xs text-gray-400">{index + 1}</span>
    };
  };

  const styles = getStatusStyles();

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
        border transition-all duration-200
        ${styles.bg} ${styles.border}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-bg-elevated'}
        ${isCurrent ? 'ring-1 ring-primary/50' : ''}
      `}
    >
      {/* 状态图标 */}
      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
        {styles.icon}
      </div>

      {/* 步骤图标和名称 */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Icon className={`w-4 h-4 flex-shrink-0 ${styles.text}`} />
        <span className={`text-sm truncate ${styles.text}`}>{name}</span>
      </div>
    </button>
  );
}

/**
 * AutoGenerationPanel 组件
 */
export default function AutoGenerationPanel({
  currentStep,
  completedSteps,
  failedStep,
  isActive,
  projectTitle,
  onStepClick,
  onCancel
}: AutoGenerationPanelProps) {
  const { t, i18n } = useTranslation(['projectSettings', 'common']);
  const generationStepNames = getLabelsByLocale(GENERATION_STEP_NAMES, i18n.language as Locale);
  /**
   * 获取步骤状态
   */
  const getStepStatus = (step: GenerationStepType): ProjectSettingStepStatus => {
    if (completedSteps.includes(step)) {
      return ProjectSettingStepStatus.COMPLETED;
    }
    if (failedStep === step) {
      return ProjectSettingStepStatus.FAILED;
    }
    if (currentStep === step) {
      return ProjectSettingStepStatus.IN_PROGRESS;
    }
    return ProjectSettingStepStatus.PENDING;
  };

  /**
   * 判断步骤是否禁用
   * 已完成的步骤可以点击查看，正在进行和未开始的步骤不可点击
   */
  const isStepDisabled = (step: GenerationStepType): boolean => {
    if (completedSteps.includes(step)) {
      return false; // 已完成的步骤可以点击
    }
    if (currentStep === step) {
      return false; // 当前步骤可以点击查看
    }
    return true; // 未开始的步骤不可点击
  };

  return (
    <div className="w-[260px] bg-bg-page border-r border-border-subtle flex flex-col h-full">
      {/* 项目标题 */}
      <div className="px-4 py-3 border-b border-border-subtle">
        <h3 className="text-base font-medium text-gray-50 truncate">
          {projectTitle || t('autoGen.defaultProjectTitle')}
        </h3>
        <p className="text-xs text-gray-400 mt-1">
          {isActive ? t('autoGen.generatingSettings') : t('autoGen.completedOrPaused')}
        </p>
      </div>

      {/* 进度指示器 */}
      <div className="px-4 py-3 border-b border-border-subtle">
        <ProgressIndicator
          completed={completedSteps.length}
          total={TOTAL_GENERATION_STEPS}
          t={t}
        />
      </div>

      {/* 步骤列表（不包含项目信息） */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {DISPLAY_STEPS.map((step, index) => (
          <StepItem
            key={step}
            step={step}
            index={index}
            status={getStepStatus(step)}
            isCurrent={currentStep === step}
            onClick={() => onStepClick(step)}
            disabled={isStepDisabled(step)}
            stepNames={generationStepNames}
          />
        ))}
      </div>

      {/* 取消按钮 */}
      {isActive && (
        <div className="p-3 border-t border-border-subtle">
          <button
            onClick={onCancel}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 
              bg-bg-elevated hover:bg-bg-surface 
              text-gray-300 hover:text-gray-50
              rounded-lg text-sm font-medium transition-colors
              border border-border-default cursor-pointer"
          >
            <XMarkIcon className="w-4 h-4" />
            {t('autoGen.cancelAutoGen')}
          </button>
        </div>
      )}
    </div>
  );
}
